import React from 'react';
import { Typography } from '@material-ui/core';
import cs from 'classnames';
import { BLOCK_PROPERTIES, requiredKey, type BlockProperty } from '../../../lib/constants';
import type { IBlock } from '../../../store/senderBuild/types';
import Checkbox from '../../common/Checkbox';

interface IProps {
  changeProperty: (key: string, flipValue: boolean) => void;
  blockTypeProperties: Array<BlockProperty['key']>;
  firstBlock?: IBlock;
  isBlockToolbar?: boolean;
  selectedBlocks?: IBlock[];
}

export function BlockProperties(props: IProps) {
  const { changeProperty, blockTypeProperties: propertyKeys, firstBlock, isBlockToolbar, selectedBlocks } = props;

  const areMultipleProperties = propertyKeys && propertyKeys.length > 1;

  let someBlocksHavePropertyApplied = false;
  let allBlocksHaveEqualPropertyValue = false;

  function getPropertyCheckedValue(blockProperty: BlockProperty) {
    if (selectedBlocks?.length) {
      const firstBlockProperty = selectedBlocks![0][blockProperty.key];
      allBlocksHaveEqualPropertyValue = selectedBlocks.every(
        (block) => block[blockProperty.key] === firstBlockProperty
      );
      someBlocksHavePropertyApplied = selectedBlocks.some((block) => block[blockProperty.key]);

      return blockProperty.flipValue && allBlocksHaveEqualPropertyValue
        ? !someBlocksHavePropertyApplied
        : someBlocksHavePropertyApplied;
    }

    if (!firstBlock) return false;
    const checked = !!firstBlock[blockProperty.key];
    return blockProperty.flipValue ? !checked : checked;
  }

  function isPropertyDisabled(blockProperty: BlockProperty): boolean {
    const blocks = selectedBlocks?.length ? selectedBlocks : firstBlock ? [firstBlock] : [];

    switch (blockProperty.key) {
      case 'readOnly':
        //In Forms, ClientType fields are used for text fields and checkboxes,
        // but we are not passing the allowOptionalOverride setting for checkboxes.
        // These will always default as allowOptionalOverride=true in DigiSign.
        return blocks.some((b) => b.groupId || (b.blockType === 'Text' && b.allowOptionalOverride === false));
      case 'required':
        // was originally: firstBlock?.readOnly || selectedBlocks?.every((block) => block.readOnly && block.blockType === BLOCK_TYPE_KEYS.TEXT)
        // if allowOptionalOverride is undefined, allow overriding (default: true)
        return blocks.some((b) => b.readOnly || b.allowOptionalOverride === false);
      default:
        return false;
    }
  }

  return (
    <section
      id="propertiesSection"
      className={cs(
        isBlockToolbar ? 'flex px-4 pt-1.5 pb-4' : 'px-7 py-4',
        isBlockToolbar && areMultipleProperties ? 'justify-around ml-0' : 'justify-start ml-4'
      )}
    >
      {!isBlockToolbar && <Typography variant="body2">Properties</Typography>}
      {propertyKeys.map((propertyKey) => {
        const blockProperty = BLOCK_PROPERTIES[propertyKey];

        return (
          <Checkbox
            isBlockToolbarCheckbox={isBlockToolbar}
            key={blockProperty.key}
            className={isBlockToolbar ? 'text-blue-800 my-2 mx-0' : 'text-blue-800 w-full my-3 mx-0'}
            label={isBlockToolbar && blockProperty.key === requiredKey ? 'Optional' : blockProperty.label}
            checked={getPropertyCheckedValue(blockProperty)}
            intermediate={
              !!selectedBlocks?.length &&
              selectedBlocks.length > 1 &&
              someBlocksHavePropertyApplied &&
              !allBlocksHaveEqualPropertyValue
            }
            onClick={(checked: boolean) =>
              changeProperty(blockProperty.key, blockProperty.flipValue ? !checked : checked)
            }
            disabled={isPropertyDisabled(blockProperty)}
            data-spec={blockProperty.key === requiredKey ? 'toggle-optional' : `toggle-${blockProperty.key}`}
          />
        );
      })}
    </section>
  );
}
